import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { ReclamationAccessPolicy } from 'graphql-ts-types';
import type { SpaceProps } from 'styled-system';
import { space } from 'styled-system';

import NextSteps from 'common/components/NextSteps';
import { TrackingEventElement, TrackingEventInteraction } from 'common/enums/Reclamation';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { NextStep } from 'common/types/next-steps';
import { dispatchReclamationRecapCardListEvent } from '../../utils/tracking';
import type { AdditionalInfoProps } from './AdditionalInfo';
import AdditionalInfo from './AdditionalInfo';
import Comment from './Comment';
import type { ProductDetailsProps } from './ProductDetails';
import ProductDetails from './ProductDetails';
import type { ReasonProps } from './Reason';
import Reason from './Reason';
import Separator from './Separator';
import type { SolutionProps } from './Solution';
import Solution from './Solution';

type WithoutClickHandler<T> = Omit<T, 'onClick'>;
type SectionsProps = Omit<ProductDetailsProps, 'itemId' | 'onItemDelete'> &
  WithoutClickHandler<ReasonProps> &
  WithoutClickHandler<SolutionProps> &
  WithoutClickHandler<AdditionalInfoProps>;
type StepEditHandler = (itemId: string) => void;
type CommentEditHandler = (itemId: string, comment: string) => Promise<void>;
type ItemDeleteHandler = (itemId: string) => Promise<void>;

interface CardProps extends SectionsProps {
  id: string;
  vendorOrderId: string;
  vendorOrderIdPolicy: ReclamationAccessPolicy;
  isReadOnly: boolean;
  isCancellation: boolean;
  nextSteps: Array<Nullable<NextStep>>;
  reclamationId: string;
  commentPolicy?: ReclamationAccessPolicy;
  comment?: string;
  claimedQuantity: number;
  isExternalMarketplace?: boolean;
  onReasonEdit?: StepEditHandler;
  onAdditionalInfoEdit?: StepEditHandler;
  onSolutionEdit?: StepEditHandler;
  onCommentEdit?: CommentEditHandler;
  onItemDelete?: ItemDeleteHandler;
}

const CONTENT_SPACE = ['sp_12', 'sp_16', null, 'sp_24'];

const StyledNextSteps = styled(NextSteps)<SpaceProps>`
  ${space};
`;

const nextStepsStyles = {
  pt: CONTENT_SPACE,
};

// eslint-disable-next-line max-lines-per-function, complexity
const Card = ({
  id,
  imagePath,
  title,
  subtitle,
  solution,
  reason,
  partNumbers,
  resources,
  commentPolicy,
  comment,
  vendorOrderId,
  claimedQuantity,
  nextSteps,
  isDisabled,
  isReadOnly,
  isCancellation,
  isExternalMarketplace,
  onItemDelete,
  onReasonEdit,
  onSolutionEdit,
  onAdditionalInfoEdit,
}: CardProps) => {
  const { t } = useLocalization();
  const handleReasonEdit = () => {
    dispatchReclamationRecapCardListEvent(TrackingEventInteraction.EditClaimReasons);

    return onReasonEdit?.(id);
  };
  const handleSolutionEdit = () => {
    dispatchReclamationRecapCardListEvent(TrackingEventInteraction.EditClaimSolutions);

    return onSolutionEdit?.(id);
  };
  const handleAdditionalInfoEdit = () => {
    dispatchReclamationRecapCardListEvent(TrackingEventInteraction.EditClaimAdditionalInfo);

    return onAdditionalInfoEdit?.(id);
  };
  const handleItemDelete = () => {
    dispatchReclamationRecapCardListEvent(
      TrackingEventInteraction.DeleteClaim,
      TrackingEventElement.Button
    );

    return onItemDelete?.(id);
  };

  const shouldDisplayComment =
    !isExternalMarketplace &&
    commentPolicy !== ReclamationAccessPolicy.Disallowed &&
    !isCancellation;

  return (
    <div data-testid="card">
      <ProductDetails
        claimedQuantity={claimedQuantity}
        imagePath={imagePath}
        title={title}
        subtitle={subtitle}
        isDisabled={isDisabled}
        {...(!!onItemDelete
          ? {
              onItemDelete: handleItemDelete,
            }
          : {})}
      />
      {!isCancellation && reason && !isExternalMarketplace && (
        <>
          <Separator />
          <Solution
            solution={solution}
            isDisabled={isDisabled}
            {...(!!onSolutionEdit
              ? {
                  onEdit: handleSolutionEdit,
                }
              : {})}
          />
        </>
      )}
      <Separator />
      <Reason
        reason={reason}
        isDisabled={isDisabled}
        {...(!!onReasonEdit
          ? {
              onEdit: handleReasonEdit,
            }
          : {})}
      />
      {reason && !isExternalMarketplace && (
        <AdditionalInfo
          partNumbers={partNumbers}
          resources={resources}
          isDisabled={isDisabled}
          vendorOrderId={vendorOrderId}
          {...(onAdditionalInfoEdit
            ? {
                handleEdit: handleAdditionalInfoEdit,
              }
            : {})}
        />
      )}
      {shouldDisplayComment && (
        <Comment
          value={comment}
          isDisabled={isDisabled}
          {...(onAdditionalInfoEdit
            ? {
                handleEdit: handleAdditionalInfoEdit,
              }
            : {})}
        />
      )}
      <StyledNextSteps
        items={nextSteps}
        {...(isReadOnly
          ? {
              title: t('h24_next_steps'),
              borderTopStyle: 'solid',
              borderTopWidth: 'small',
              borderTopColor: 'sys.neutral.border.weak',
              pt: ['sp_12', null, null, 'sp_16'],
            }
          : {
              isCollapsible: true,
              mt: 'sp_0',
            })}
        {...nextStepsStyles}
      />
    </div>
  );
};

export default Card;
